import Toggles from '@domain/toggles/Toggles';
import { getModifier } from '../catalog/ArticleCollection';
import { variationType } from "../catalog/catalog";
import { hexDictionary } from "../data/Constants";
import Table, { LightVariationsInterface } from '../data/Table';
import { PLUSMINUS } from '../catalog/Product';
import Base, { parseDomainData } from "./Base";
import Cluster from "./Cluster";
import { baseAttribute, baseAttributes, catalogAttribute, DomainModelData, LightingEffectType, LightRender, LightSize, type statusKeyType } from "./composition";
import { lightProductTypology } from '../data/Constants';

export interface VibiaLightRenderAttributes {
	scale: number[],
	intensity: number,
	decay: number,
	penumbra: number,
	light?: LightRender
	size?: LightSize
	// Missing some JSON compositor attributes (initialCamPosition, targetPosition, etc...)
}

export interface VibiaLightParams {
	id?: string,
	code: string,
	attributes: any,
	renderAttributes: VibiaLightRenderAttributes
}

// eslint-disable-next-line no-undef
export interface VibiaLightProps extends Omit<VibiaLightParams, 'id'> {
	id?: string
	info?: {
		subfamily: DomainModelData['subfamily']
		catalog: DomainModelData['catalog']
		familyName: DomainModelData['familyName']
		applicationLocation: DomainModelData['applicationLocation']
	},
	technicalData?: DomainModelData['technicalData']
	textSalesData?: DomainModelData['textSalesData']
	attributeOptions?: {
		controls: DomainModelData['controls']
		temperatures: DomainModelData['temperatures']
		finishes: DomainModelData['finishes']
		installations: DomainModelData['installations']
	}
	renderAttributes: VibiaLightRenderAttributes
}

type AttributeVariationsType = VibiaLightProps['attributeOptions']['controls'] |
	VibiaLightProps['attributeOptions']['temperatures'] |
	VibiaLightProps['attributeOptions']['finishes'] |
	VibiaLightProps['attributeOptions']['installations']

type AttributeVariationType = AttributeVariationsType[number]

const lightAttributes = ['color', 'temperature', 'control', 'installation']

const blackListedProps = ['info', 'technicalData', 'textSalesData', 'attributeOptions', 'renderAttributes']

const attributesDictionary = {
	color: 'finishes',
	temperature: 'temperatures',
	control: 'controls',
	installation: 'installations'
}

const deepCopy = obj => JSON.parse(JSON.stringify(obj)) // change to structuredClone when node is updated to v17
export class Light extends Base {
	type = 'Light'
	static className = 'Light'
	domainData: DomainModelData = null
	constructor(vibiaLightData: VibiaLightParams, parent?: Base, attributes?: baseAttributes, data?: DomainModelData) {
		super(parent, vibiaLightData.id, attributes, data)

		if (vibiaLightData.attributes?.installation) {
			this.setInstallation(vibiaLightData.attributes.installation)
			delete vibiaLightData.attributes.installation
		}

		this.props = deepCopy({
			...super.getProps(),
			attributes: vibiaLightData.attributes ?? {},
			...vibiaLightData,
			renderAttributes: {
				...super.getProps().renderAttributes ?? {
					"scale": [1, 1, 1],
					"intensity": 2,
					"decay": 2,
					"penumbra": 1
				},
				...vibiaLightData.renderAttributes
			}
		})

		if (this.wayOfJson) {
			if (!this.getAttribute("color")) {
				const defaultColor = this.getDefaultAttributeValue('color')
				this.setAttribute('color', defaultColor)
			}

			const controlOptions = this.getAttributeVariations('control')
			if (controlOptions.length === 1) {
				const optionValue = this.getDefaultAttributeValue('control')

				this.setAttribute('control', optionValue)
			}

			const temperatureOptions = this.getAttributeVariations('temperature')
			if (temperatureOptions.length === 1) {
				const optionValue = this.getDefaultAttributeValue('temperature')
				this.setAttribute('temperature', optionValue)
			}

			const installationOptions = this.getAttributeVariations('installation')
			if (installationOptions.length === 1 && this.getParent()) {
				const optionValue = this.getDefaultAttributeValue('installation')
				this.setAttribute('installation', optionValue)
			}
		}

		this.reload()
	}

	reload() {
		try {
			this.setSize(this.getLightOptions().size)
		} catch (e) {
			this.setSize({ width: 0, depth: 0, height: 0 })
		}
		super.reload()
		return this
	}

	getModel() {
		return this.getCode()
	}

	getSubFamilyName() {
		return this?.props?.info?.familyName
	}

	getLightingEffect() {
		return this?.data?.lightingEffect as LightingEffectType
	}

	getLuminaryShape() {
		return this?.data?.luminaryShape
	}

	getLightingEffectLevel() {
		const lightingEffect = this?.data?.lightingEffect as LightingEffectType

		let level = 0
		if (lightingEffect?.lvl4) {
			level = 4
		} else if (lightingEffect?.lvl3) {
			level = 3
		} else if (lightingEffect?.lvl2) {
			level = 2
		} else if (lightingEffect?.lvl1) {
			level = 1
		}

		return level
	}

	getSubFamily() {
		return this?.props?.info?.subfamily
	}

	getCode(): string {
		return this.props.code
	}

	getProp(prop) {
		return this.props[prop]
	}

	convertToPsa(): Base {

		const isPSA = this.isPsaApplicable()
		if (!isPSA) {
			return this
		}

		const psaVariations = this.getPsaVariations()
		for (const attribute in psaVariations) {
			if (psaVariations[attribute].length === 0) {
				continue;
			}
			const newAtribute = Light.parseAttributeValueFromVariationOption(attribute as variationType, psaVariations[attribute][0])
			this.setAttribute(attribute as variationType, newAtribute)

			const parsedAttribute = attributesDictionary[attribute] 
			this.props.attributeOptions[parsedAttribute] = this.getProp('attributeOptions')[parsedAttribute].filter(variation => variation.isPsa === 1)
		}
		this.props.isPsa = true
		return this
	}

	getPsa(): boolean {
		return this.props.isPsa === true
	}
	

	convertoToPA() {
		const isPSA = this.isPsaApplicable()
		if (!isPSA) {
			return this
		}

		const psaVariations = this.getPsaVariations()
		for (const attribute in psaVariations) {
			if (psaVariations[attribute].length === 0) {
				continue;
			}
			const parsedAttribute = attributesDictionary[attribute] 
			this.props.attributeOptions[parsedAttribute] = this.getProp('attributeOptions')[parsedAttribute].filter(variation => variation?.isPsa !== 1)
		}
		return this
	}


	getPsaVariations() {
		const attributesToCheck: variationType[] = ['control', 'temperature', 'installation'];
		const psaVariationsByType = {}

		attributesToCheck.forEach(attribute => {
			const variations = this.getAttributeVariations(attribute);
			const psaVariations = variations.filter((variation: any) => variation?.isPsa === 1);
			psaVariationsByType[attribute] = psaVariations;
		});

		return psaVariationsByType;
	}


	isPsaApplicable(): boolean {
		const attributesToCheck: variationType[] = ['control', 'installation']
		let isPsa = false
		attributesToCheck.forEach(attribute => {
			if (this.getAttributeVariations(attribute).some((variation: any) => variation.isPsa === 1)) {
				isPsa = true
			}
		})
		return isPsa
	}

	getAttributeKeys() {
		return [...super.getAttributeKeys(), 'installation']
	}

	getCatalogAttributeKeys() {
		return super.getCatalogAttributeKeys().concat(['color', 'temperature', 'control', 'installation'])
	}

	getAttribute(key: baseAttribute, withModifier?: boolean) {
		switch (key) {
			case 'installation':
				return this.getInstallation(withModifier)
			// const lightOptions = this.getLightOptions()
			// return lightOptions.canopy[this.getInstallation()]?.reference || lightOptions.installations.options.find(installation => installation.reference == this.getInstallation())?.reference
			default:
				return super.getAttribute(key, withModifier)
		}
	}

	getAttributes(withModifier?: boolean) {
		const attributes = super.getAttributes(withModifier)
		const installation = this.getInstallation(withModifier)
		const result = { ...attributes }
		if (installation) {
			result.installation = installation
		}
		return result
	}

	/* FIXME -> Evitar los hacks de este estilo */
	/* No cambiar comportamiento para atributo instalación -> que lo intercepte el padre que tenga que interceptarlo (cluster) cuando se lance requestSetAttribute */
	/* No debería ser necesario comprobar el label del articulo ni el value (Fix temporal que se hizo) */
	setAttribute(attribute: baseAttribute, value: any, own?: any): this {
		if (attribute == 'installation') {
			this.setInstallation(value)
			return this
		}

		const isChangeValid = this.isNewAttributeValid(attribute, value)
		if (!isChangeValid)
			return this

		return super.setAttribute(attribute, value, own)
	}

	static parseModifiersFromReference(referenceBase) {
		let referenceType = 'normal'
		const dashedReference = referenceBase.includes('-')
		if (dashedReference)
			referenceType = 'dashed'
		if (referenceBase.trim().length == 6)
			referenceType = 'digits6'
		if (referenceBase.trim().length == 7)
			referenceType = 'dasheddigits6'

		const hasInstallation = referenceBase.includes('_')

		const positions = {
			dashed: {
				color: 5,
				control: 9,
				temperature: 8,
				...(hasInstallation && { installation: 11 })
			},
			normal: {
				color: 4,
				control: 8,
				temperature: 7,
				...(hasInstallation && { installation: 10 })
			},
			dasheddigits6: {
				color: 5
			},
			digits6: {
				color: 4
			}
		}

		const position = positions[referenceType]

		const lengths = {
			color: 2,
			control: 1,
			temperature: 1,
			installation: 2
		}

		const color = position['color'] ? referenceBase.slice(position['color'], position['color'] + lengths['color']) : null
		const control = position['control'] ? referenceBase.slice(position['control'], position['control'] + lengths['control']) : null
		const temperature = position['temperature'] ? referenceBase.slice(position['temperature'], position['temperature'] + lengths['temperature']) : null
		const installation = position['installation'] ? referenceBase.slice(position['installation'], position['installation'] + lengths['installation']) : null

		return { color, control, temperature, installation }
	}

	setAttributesByReference(reference: string) {
		const validReferenceLength = [6, 7, 9, 10, 12, 13]
		const invalidReference = !reference || !validReferenceLength.includes(reference.length)
		if (invalidReference)
			throw `${reference} is not a valid reference.`

		const modifiers = Light.parseModifiersFromReference(reference)

		const installationOptions = this.getAttributeVariations('installation')
		if (installationOptions.length && !modifiers.installation) {
			const defaultInstallation = installationOptions.find(element => element.type === "default")
			modifiers.installation = defaultInstallation.modifier
		}

		const attributes: variationType[] = ['color', 'temperature', 'control', 'installation']
		attributes.forEach(attribute => {
			if (attribute == 'installation' && !modifiers[attribute]) return // Go to next iteration, installation is not compulsory

			const variation = this.getAttributeVariation(attribute, modifiers[attribute])
			if (!variation)
				throw `Cannot get ${attribute} for reference ${reference}. This may not be a valid reference.`

			const newAttributeValue = Light.parseAttributeValueFromVariationOption(attribute, variation)
			this.setAttribute(attribute, newAttributeValue)
		})

		return this
	}

	deleteAttribute(attribute: baseAttribute): this {
		if (attribute == 'installation') {
			this.deleteInstallation()
			return this
		}
		return super.deleteAttribute(attribute)
	}

	getAttributesVariations() {
		return super.getAttributesVariations()
	}

	getAttributeVariations(attribute: catalogAttribute): AttributeVariationsType {
		return super.getAttributeVariations(attribute)
	}

	getAttributeVariation(attribute: catalogAttribute, modifier?: string): any {
		return super.getAttributeVariation(attribute, modifier)
	}

	static parseAttributeValueFromVariationOption(attribute: variationType, option: AttributeVariationType): string {
		return super.parseAttributeValueFromVariationOption(attribute, option)
	}

	getDefaultAttributeValue(attribute: variationType) {
		if (!this.wayOfJson) {
			return super.getDefaultAttributeValue(attribute)
		} else {
			const options = this.getAttributeVariations(attribute)
			const defaultOption = options.find(element => element.type === "default")

			return Light.parseAttributeValueFromVariationOption(attribute, defaultOption)
		}
	}

	isNewAttributeValid(attribute: baseAttribute, newValue): boolean {
		if (!lightAttributes.includes(attribute) || !this.wayOfJson) {
			return super.isNewAttributeValid(attribute, newValue)
		}

		const variations = this.getAttributeVariations(attribute as Exclude<catalogAttribute, 'pendantHeight'>)
		return variations.some(variation => variation.modifier == getModifier(newValue)) && super.isNewAttributeValid(attribute, newValue)
	}

	resetInvalidAttributes() {
		const invalidAttributes = this.getInvalidAttributes()
		invalidAttributes.forEach(invalidAttribute => this.resetAttribute(invalidAttribute))

		return super.resetInvalidAttributes()
	}

	deleteInvalidAttributes() {
		const invalidAttributes = this.getInvalidAttributes()
		invalidAttributes.forEach(invalidAttribute => this.deleteAttribute(invalidAttribute))

		return super.deleteInvalidAttributes()
	}

	getOrigin(): string {
		return this.getLightOptions().origin
	}

	getAvailableCanopies() {
		const parent = this.getParent() as Cluster
		return parent && parent.getAvailableCanopies
			? parent.getAvailableCanopies() || []
			: []
	}

	getRenderAttribute(key: string) {
		return this.props.renderAttributes[key]
	}

	getRenderAttributes(): VibiaLightRenderAttributes {
		return deepCopy(this.props.renderAttributes)
	}

	setRenderAttribute(attribute: string, value): this {
		this.props.renderAttributes[attribute] = value
		return this
	}

	getLights() {
		const parent = this.getParent()
		if (parent) {
			return parent.getElement(this)
		} else {
			return { element: this, options: {} }
		}
	}

	getOptionVariations(attribute: variationType) {
		return super.getOptionVariations(attribute)
	}

	isAttributeCompatible(attribute, newValue) {
		return super.isAttributeCompatible(attribute, newValue)
	}

	getLightOptions(): LightVariationsInterface {
		if (!this.wayOfJson) {
			const articleCollection = this.getArticleCollection()
			const code = this.getCode()
			return Table.getLightVariations(code, articleCollection)
		} else {
			let controlOptions = this.getAttributeVariations('control')
			const baseColor = this.getAttributeVariations('color').find(variation => variation.type == 'default')
			const colorsArray = this.getAttributeVariations('color').filter(variation => ['default', 'variation'].includes(variation.type))
			const colors = colorsArray.map(variation => Light.parseAttributeValueFromVariationOption('color', variation))

			const light = this.getRenderAttributes().light
			const colorButtons = colorsArray.map(elem => hexDictionary[elem.modifier])
			const canopy = this.getRenderAttribute('canopy') ?? {}
			const extraColorsArray = this.getAttributeVariations('color').filter(variation => variation.type == 'optional')

			// Siempre usar la lógica del super
			const baseElementOptions = super.getElementOptions()
			const extraColors = baseElementOptions.extraColors
			const installationsV2 = baseElementOptions.installationsV2
			const temperatures = baseElementOptions.temperatures
			const temperaturesV2 = baseElementOptions.temperaturesV2
			let controls = baseElementOptions.controls
			let controlsV2 = baseElementOptions.controlsV2

			if (!Toggles.isEnabled("filterControlMforCP")) {
				if (Toggles.isEnabled('filterControlM')) {
					controls = controls.filter(elem => elem.charAt(0) !== 'M')
					controlsV2.options = controlsV2.options.filter(elem => elem.modifier !== "M")
					controlsV2.extraOptions = controlsV2.extraOptions.filter(elem => elem.modifier !== "M")
				}
			}

			const extraColorButtons = extraColorsArray.map(elem => hexDictionary[elem.modifier])

			installationsV2.options.forEach(function (installationOption) {
				if (canopy[installationOption.modifier]) {
					canopy[installationOption.modifier].description = installationOption.description
				}
			})

			const colorsV2 = {
				options: colorsArray.map(elem => {
					return {
						value: Light.parseAttributeValueFromVariationOption('color', elem),
						label: elem?.description?.en,
						buttonColor: hexDictionary[elem.modifier],
						compatible: {
							value: this.isAttributeCompatible('color', Light.parseAttributeValueFromVariationOption('color', elem)),
							reason: 'This combination is incompatible.'
						},
						...elem
					}
				}),
				extraOptions: extraColorsArray.map(elem => {
					return {
						value: Light.parseAttributeValueFromVariationOption('color', elem),
						label: elem?.description?.en,
						buttonColor: hexDictionary[elem.modifier],
						compatible: {
							value: this.isAttributeCompatible('color', Light.parseAttributeValueFromVariationOption('color', elem)),
							reason: 'This combination is incompatible.'
						},
						...elem
					}
				}),
				base: {
					value: Light.parseAttributeValueFromVariationOption('color', baseColor),
					label: baseColor?.description?.en,
					buttonColor: hexDictionary?.[baseColor?.modifier],
					compatible: {
						value: this.isAttributeCompatible('color', Light.parseAttributeValueFromVariationOption('color', baseColor)),
						reason: 'This combination is incompatible.'
					},
					...baseColor,
				}
			}

			const size = this.getRenderAttribute('size') ?? {}
			const origin = 'top-center'
			return {
				colors,
				extraColors,
				colorsV2,
				temperatures,
				controls,
				light,
				colorButtons,
				extraColorButtons,
				canopy,
				controlsV2,
				temperaturesV2,
				installationsV2,
				size,
				origin
			} as LightVariationsInterface
		}
	}

	getStatus(statusKey: statusKeyType) {
		return this.data.status?.[statusKey]
	}

	getInfo() {
		const info = super.getInfo()
		const installation = this.getInstallation(false)
		const attributes = { ...info.attributes, installation }
		const article = { ...info.article, installation, type: "light" }
		const consumed_power = this.getTechnicalDataByKey("watts")?.value

		return {
			...info,
			type: 'light',
			attributes,
			article,
			consumed_power
		}
	}

	getComputedReference() {
		const superComputedReferences = super.getComputedReference()
		if (!superComputedReferences) return null
		let reference16 = superComputedReferences.reference16
		let reference21 = superComputedReferences.reference21

		const isPlusminusEssentialsUSA = Toggles.isEnabled("noDashPlusminusEssentialsUsa")
			&& this.props.info?.catalog == 21
			&& this.props.info?.subfamily == PLUSMINUS.ESSENTIALS_SUBFAMILY_ID
		if (isPlusminusEssentialsUSA) {
			reference21 = reference16
		}

		return {
			reference16,
			reference21
		}
	}

	getInstallation(withModifier?: boolean) {
		return this.getParent()?.getInstallation(withModifier)
	}

	getProductTypology() {
		return ""
	}

	getProductTypologies() {
		const types = super.getProductTypologies();
		return types ?? lightProductTypology;
	}

	setInstallation(installation) {
		return this.getParent()?.setInstallation(installation)
	}

	deleteInstallation() {
		if (this.getParent()) {
			this.getParent().deleteInstallation()
		}
		return this
	}

	remove() {
		this.getParent().remove(this)
	}

	toJSON() {
		return {
			...super.toJSON((props => {
				return Object.keys(props).reduce((obj, prop) => {
					if (blackListedProps.includes(prop)) return obj
					return {
						...obj,
						[prop]: props[prop]
					}
				}, {})
			}))
		}
	}

	fromJSON(json, options?: any, instance?: Base, modelData?: DomainModelData) {
		return Light.fromJSON(json, options, instance, modelData)
	}
	static fromJSON(json, options?: any, instance?: Base, modelData?: DomainModelData) {
		if (options?.domainFactory && !instance && !modelData) {
			const model = json.props.code
			modelData = options.domainFactory.familyData?.[model]
		}
		return super.fromJSON(json, options, instance ?? new this(json.props, null, null, json.domainData ?? modelData))
	}

	filterControlsByModifier(controls: any, excludedModifiers: string[] = ['M']): string[] {
		return controls?.filter(control => !excludedModifiers.includes(control.modifier)) ?? []
	}


}

export default Light